import { useOrganizations } from '@hotjar/organization';
import {
  Button,
  Checkbox,
  Field,
  Loader,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Text,
  useNotify,
} from '@hotjar/hotsauce';
import type { FormEventHandler } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useCurrentUser } from '@hotjar/user';
import { useMutation } from 'react-query';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import { getCurrentSiteFromLocalStorage, getUsersFromOrganizations } from '@hotjar/user-resources';
import { getRoute, navigateTo } from '@hotjar/webapp/utils/routing';
import { useOwnedAccount } from '@hotjar/account';
import { useTranslation } from '@hotjar/translations';
import { refreshFromServer } from '../../../../domains/common/user-data';
import { ErrorText } from '../../../../components/ErrorText';
import { mutateAccountOwnership } from './query';

const PASSWORD_ERROR = '"password" was incorrect or not supplied.';

interface TransferOwnershipModalProps {
  isVisible: boolean;
  onClose: () => void;
}

export const TransferOwnershipModal = (props: TransferOwnershipModalProps): JSX.Element => {
  const { isVisible, onClose } = props;

  const { t } = useTranslation('account', { keyPrefix: 'transfer-ownership' });
  const { showToast } = useNotify();

  const { isLoading: isLoadingCurrentUser, data: currentUser } = useCurrentUser();
  const { isLoading: isLoadingOrgs, data: orgs } = useOrganizations(currentUser?.id);
  const { isLoading: isLoadingAccount, data: account } = useOwnedAccount();

  const [newOwnerId, setNewOwnerId] = useState<number | undefined>(undefined);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [confirmTransfer, setConfirmTransfer] = useState<boolean>(false);

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);
  const [isPasswordIncorrect, setIsPasswordIncorrect] = useState<boolean>(false);

  const isOwnerIdValid = typeof newOwnerId === 'number';
  const isCurrentPasswordValid = !isPasswordIncorrect && currentPassword.length > 0;
  const isConfirmTransferValid = confirmTransfer;
  const isFormValid = isOwnerIdValid && isCurrentPasswordValid && isConfirmTransferValid;

  const isLoadingNewOwners = isLoadingCurrentUser || isLoadingOrgs;

  const closeAndReset = useCallback(() => {
    setNewOwnerId(undefined);
    setCurrentPassword('');
    setConfirmTransfer(false);
    setHasAttemptedSubmit(false);
    onClose();
  }, [onClose]);

  const users = useMemo(() => {
    if (!orgs || !currentUser) return [];

    return getUsersFromOrganizations(orgs)
      .filter((user) => user.id !== currentUser.id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orgs, currentUser]);

  const { mutate, isLoading: isLoadingMutation } = useMutation(mutateAccountOwnership, {
    async onSuccess(response) {
      if (response.success) {
        showToast({
          kind: 'success',
          text: t('transfer-success'),
        });
        closeAndReset();
        await refreshFromServer();
        const currentSite = getCurrentSiteFromLocalStorage();
        if (!currentSite) return;
        navigateTo(getRoute(OVERVIEW.BASE.path, { siteId: currentSite.id }));
      } else if (response.error === PASSWORD_ERROR) {
        setIsPasswordIncorrect(true);
        showToast({
          kind: 'danger',
          text: t('transfer-incorrect-password'),
        });
      } else {
        showToast({
          kind: 'danger',
          text: t('transfer-failure'),
        });
      }
    },
    onError() {
      showToast({
        kind: 'danger',
        text: t('transfer-failure'),
      });
    },
  });

  const transferOwnership: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      setHasAttemptedSubmit(true);
      if (!isFormValid || !account?.id) return;
      mutate({
        accountId: account.id,
        newOwnerId,
        password: currentPassword,
      });
    },
    [isFormValid, account?.id, currentPassword, newOwnerId, mutate],
  );

  return (
    <Modal onClose={closeAndReset} size="m" isOpen={isVisible} contentLabel={t('title')}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <Stack direction="column" spacing="x2">
          <Text textType="body2" as="p">
            {t('info')}
          </Text>
          <div>
            <Text textType="body2" as="p">
              {t('restriction.info')}
            </Text>
            <ol>
              <Text as="li" textType="body2">
                {t('restriction.rules.0')}
              </Text>
              <Text as="li" textType="body2">
                {t('restriction.rules.1')}
              </Text>
              <Text as="li" textType="body2">
                {t('restriction.rules.2')}
              </Text>
            </ol>
          </div>
        </Stack>
        <form
          id="forms.transferAccount"
          name="forms.transferAccount"
          noValidate
          onSubmit={transferOwnership}
        >
          <Stack preset="form">
            <Field size="full" labelText={t('new-owner-label')} inputId="newUser" isRequired>
              {isLoadingNewOwners && <Loader size="m" />}
              {!isLoadingNewOwners && users.length === 0 && (
                <Text textType="subtitle2">{t('no-new-owners')}</Text>
              )}
              {!isLoadingNewOwners && users.length > 0 && (
                <Select
                  value={newOwnerId}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === '?') {
                      setNewOwnerId(undefined);
                    } else {
                      setNewOwnerId(Number(value));
                    }
                  }}
                  id="newUser"
                  name="newUser"
                  size="m"
                  isRequired
                >
                  <option value="?">{t('new-owner-placeholder')}</option>
                  {users.map((user) => (
                    <option value={user.id} key={user.id}>
                      {`${user.name} - ${user.email}`}
                    </option>
                  ))}
                </Select>
              )}
              {hasAttemptedSubmit && !isOwnerIdValid && <ErrorText>{t('required')}</ErrorText>}
            </Field>
            <Field size="full" labelText={t('password-label')} inputId="password" isRequired>
              <PasswordInput
                value={currentPassword}
                onChange={(e) => {
                  setIsPasswordIncorrect(false);
                  setCurrentPassword(e.target.value);
                }}
                id="password"
                name="password"
                placeholder={t('password-placeholder')}
                size="m"
                isRequired
              />
              {hasAttemptedSubmit && isPasswordIncorrect && (
                <ErrorText>{t('transfer-incorrect-password')}</ErrorText>
              )}
              {hasAttemptedSubmit && !isCurrentPasswordValid && !isPasswordIncorrect && (
                <ErrorText>{t('required')}</ErrorText>
              )}
            </Field>
            <Field
              inputId="confirmTransfer"
              size="full"
              titleAs="label"
              labelText={t('confirm-transfer-label')}
              isRequired
            >
              <Checkbox
                onChange={() => setConfirmTransfer((val) => !val)}
                isChecked={confirmTransfer}
                labelContent={t('confirm-transfer-label-content')}
                id="confirmTransfer"
                name="confirmTransfer"
                isRequired
              />
              {hasAttemptedSubmit && !isConfirmTransferValid && (
                <ErrorText>{t('required')}</ErrorText>
              )}
            </Field>
          </Stack>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={closeAndReset}>
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          form="forms.transferAccount"
          isDisabled={isLoadingNewOwners || isLoadingMutation || isLoadingAccount}
          isLoading={isLoadingMutation}
          kind="primary"
          id="submitAccountTransferButton"
        >
          {t('transfer')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
